import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — VISIKT" },
      {
        name: "description",
        content:
          "VISIKT Terms of Service — the agreement between VIKEEP AS and users of the VISIKT app.",
      },
      { property: "og:title", content: "Terms of Service — VISIKT" },
      {
        property: "og:description",
        content: "The agreement between VIKEEP AS and users of the VISIKT app.",
      },
      { property: "og:url", content: "https://visikt.com/terms" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://visikt.com/terms" }],
  }),
  component: TermsPage,
});

type Rich = string | Array<{ text: string; bold?: boolean; italic?: boolean }>;
type Section = { title: string; paragraphs: Rich[] };
type TermsContent = { headerLine: string; lastUpdated: string; sections: Section[] };

const HEADER_LINE_EN = "Operator: VIKEEP AS, org.nr 938 008 272, Oslo, Norway · contact@vikeep.no";
const HEADER_LINE_NB = "Tilbyder: VIKEEP AS, org.nr 938 008 272, Oslo, Norge · contact@vikeep.no";
const LAST_UPDATED_EN = "Last updated: 2 July 2026";
const LAST_UPDATED_NB = "Sist oppdatert: 2. juli 2026";

const CONTENT_EN: TermsContent = {
  headerLine: HEADER_LINE_EN,
  lastUpdated: LAST_UPDATED_EN,
  sections: [
    {
      title: "1. Definitions",
      paragraphs: [
        '1.1 "VIKEEP", "we", "us" — VIKEEP AS, the operator of the Service.',
        '1.2 "Service" / "VISIKT" — the VISIKT web application at visikt.com and related functionality.',
        '1.3 "Consumer" — a user acting for purposes outside their trade, business, craft, or profession.',
        '1.4 "Your Content" — the data you enter, including subscriptions, salary figures, and notes.',
        '1.5 "CPI Data" — official consumer price index and related statistical data used by the Service to calculate inflation-adjusted values.',
        '1.6 "Privacy Policy" — our privacy policy available in the Service, which forms part of these Terms.',
      ],
    },
    {
      title: "2. Acceptance and scope",
      paragraphs: [
        "2.1 By creating an account or using the Service you agree to these Terms and the Privacy Policy. If you do not agree, do not use the Service.",
        "2.2 These Terms, the Privacy Policy, and the pricing and plan details shown at purchase form the entire agreement between you and us for the Service.",
        "2.3 If you are a Consumer, nothing in these Terms limits rights you have under mandatory Norwegian or EEA consumer law; where a term conflicts with such rights, those rights prevail.",
        "2.4 If you visit the public pages of the Service (for example the marketing website) without creating an account, clauses 7 (Intellectual property) and 12 (Availability and disclaimers) apply to that visit as they would to a User. Simply visiting these public pages does not otherwise create a contractual relationship between you and us.",
      ],
    },
    {
      title: "3. What VISIKT is — and is not",
      paragraphs: [
        '3.1 VISIKT helps you record your subscriptions and salary and shows calculations and visualizations, including inflation-adjusted ("real") purchasing power derived from CPI Data.',
        "3.2 The Service is an informational and organizational tool only. It does not provide financial, investment, tax, accounting, pension, debt, or legal advice, and no output is a personal recommendation. All figures are produced by deterministic calculation from the data you enter and from third-party CPI Data; they may be estimates, rounded, provisional, or simplified, and CPI figures may be revised by their source after publication.",
        "3.3 Outputs are general information, not automated decisions producing legal or similarly significant effects about you, and must not be relied upon as the basis for any financial decision.",
        "3.4 You are solely responsible for verifying figures and for your own decisions. VIKEEP is not a bank, payment institution, accountant, or financial adviser, and does not hold, move, or manage your money. Where a decision matters to you, obtain independent professional advice.",
      ],
    },
    {
      title: "4. Eligibility, accounts and security",
      paragraphs: [
        "4.1 You must be at least 18 years old and able to enter a binding contract. The Service is for personal, non-commercial use, with one account per person unless we agree otherwise.",
        "4.2 You must provide accurate registration information and keep it current.",
        "4.3 You are responsible for safeguarding your credentials and for activity under your account, and must notify us promptly at contact@vikeep.no of any suspected unauthorised use.",
        "4.4 You may sign in with email and password or with a third-party provider (Google or Apple); your use of those providers is also subject to their terms.",
      ],
    },
    {
      title: "5. Your Content",
      paragraphs: [
        "5.1 As between you and us, you own Your Content. You grant us a limited licence to host, process, and display it solely to operate and provide the Service to you, as described in the Privacy Policy.",
        "5.2 You are responsible for the accuracy of Your Content; the Service's outputs are only as accurate as your inputs and the available CPI Data. Do not enter special-category personal data (for example health, political, or religious information) into free-text fields.",
        "5.3 You can export Your Content (CSV) and delete your account within the Service. On deletion we delete or anonymise Your Content without undue delay, except records we must retain by law (see clause 14 and the Privacy Policy).",
      ],
    },
    {
      title: "6. Acceptable use",
      paragraphs: [
        "6.1 You agree not to: use the Service unlawfully or in breach of these Terms; access or attempt to access other accounts or our systems without authorisation; disrupt the Service; extract or scrape data other than Your Content via features we provide; reverse-engineer the Service except to the extent this restriction is prohibited by law; resell or commercially exploit the Service without our permission; or upload unlawful content or content you have no right to provide.",
      ],
    },
    {
      title: "7. Intellectual property",
      paragraphs: [
        "7.1 The Service, including its software, design, text, and branding (VISIKT and VIKEEP marks), excluding Your Content, is owned by VIKEEP or its licensors. We grant you a limited, non-exclusive, non-transferable, revocable right to use the Service for personal, non-commercial use under these Terms.",
        "7.2 CPI Data and other third-party data are used under the rights of their respective sources and remain their property.",
      ],
    },
    {
      title: "8. Plans, pricing and payment",
      paragraphs: [
        [
          { text: "8.1 Free Plan. ", bold: true },
          {
            text: "Provided as-is and free of charge, with feature limits — currently up to three subscriptions and three years of salary history, and inflation comparison limited to the current year. If you exceed a limit and later move to the Free Plan, existing entries are preserved but not visible or editable until you upgrade again.",
          },
        ],
        [
          { text: "8.2 Pro Plan. ", bold: true },
          {
            text: "Unlocks unlimited entries, full salary history, and full inflation comparison, billed on a recurring basis monthly or yearly in Norwegian kroner (NOK) at the price shown at purchase. Prices are stated inclusive of Norwegian VAT (MVA) where applicable.",
          },
        ],
        "8.3 Payments are processed by Stripe; we do not receive or store your full card details. By subscribing you authorise us (via Stripe) to charge your chosen payment method for each billing period. If a payment fails we may retry it and may suspend or downgrade Pro access until payment succeeds.",
      ],
    },
    {
      title: "9. Renewal, price changes and cancellation",
      paragraphs: [
        [
          { text: "9.1 Automatic renewal. ", bold: true },
          {
            text: "Your Pro Plan renews automatically at the end of each billing period at the then-current price until you cancel. The price and billing interval are shown at purchase and before each renewal.",
          },
        ],
        [
          { text: "9.2 Price changes. ", bold: true },
          {
            text: "We may change Pro prices with advance notice by email or in the Service; the new price applies from your next renewal, and you may cancel before it takes effect.",
          },
        ],
        [
          { text: "9.3 Cancellation / downgrade. ", bold: true },
          {
            text: "You may cancel or downgrade at any time within the Service. Cancellation stops future renewals; your Pro access continues until the end of the period you have already paid for, after which your account becomes Free. Except as required by law or clause 10, amounts already paid for the current period are not refunded on a mid-period cancellation.",
          },
        ],
      ],
    },
    {
      title: "10. Consumer right of withdrawal (angrerett)",
      paragraphs: [
        "10.1 As a Consumer purchasing a digital service at a distance, you have a statutory right to withdraw within 14 days of concluding the agreement under the Norwegian Right of Withdrawal Act (angrerettloven).",
        [
          { text: "10.2 Express request for immediate performance. ", bold: true },
          {
            text: "Before any charge for a paid Plan, you will be asked to (a) expressly request that we begin providing the paid Service immediately, and (b) confirm that you understand you will lose your right of withdrawal once the Service has been fully delivered, and that if you withdraw after performance has begun you may owe an amount proportionate to what has already been supplied. We record this request and confirmation.",
          },
        ],
        "10.3 Where you have given that request and confirmation and delivery has begun, your right of withdrawal is lost or proportionally reduced as provided by law. Where you have not, you may withdraw within 14 days by contacting contact@vikeep.no (you may use the standard withdrawal form), and we will reimburse you as required by law without undue delay.",
        "10.4 We confirm the concluded contract — including the request and confirmation above, the price, the billing interval, and renewal terms — on a durable medium (for example by email or as a downloadable confirmation) after purchase.",
        "10.5 These statutory rights apply regardless of anything else in these Terms.",
      ],
    },
    {
      title: "11. Third-party services and provider catalogue",
      paragraphs: [
        "11.1 The Service includes a catalogue of common subscription providers and may link to third-party pages, including cancellation instructions. VIKEEP is not affiliated with, endorsed by, or sponsored by those providers; third-party names and trademarks belong to their owners and are used for identification only.",
        "11.2 We do not control third-party services and are not responsible for their content, accuracy, or availability. VISIKT does not cancel, manage, or pay for your third-party subscriptions — you must cancel directly with the provider. Links and instructions are provided for convenience and may become outdated.",
        "11.3 Sign-in and payment providers (Google, Apple, Stripe) operate under their own terms and privacy policies.",
      ],
    },
    {
      title: "12. Availability and disclaimers",
      paragraphs: [
        '12.1 To the extent permitted by law, and without limiting your mandatory consumer rights, the Service is provided "as is" and "as available." We do not warrant that it will be uninterrupted or error-free, or that any calculation or third-party figure is accurate, complete, current, or fit for your particular circumstances.',
        "12.2 We may modify, suspend, or discontinue features or the Service; where we discontinue a paid feature you are actively paying for, we will act fairly and consistently with your consumer rights. Some features may be offered as beta or preview and may change or be withdrawn.",
        "12.3 We are not liable for failure or delay caused by events beyond our reasonable control (such as outages of infrastructure or third-party providers, network failures, or acts of authorities). This does not affect your right to cancel or your statutory consumer rights.",
      ],
    },
    {
      title: "13. Liability",
      paragraphs: [
        "13.1 Nothing in these Terms excludes or limits any liability that cannot lawfully be excluded or limited under Norwegian law, including liability caused by our intent or gross negligence, and nothing affects your mandatory rights as a Consumer.",
        "13.2 Subject to 13.1, we are not liable for: (a) any financial loss, cost, or decision arising from your reliance on the Service's informational outputs; (b) indirect or consequential loss, including loss of profits, savings, goodwill, or data; or (c) loss arising from third-party services, from Your Content entered incorrectly, or from your failure to cancel a third-party subscription.",
        "13.3 Subject to 13.1, and to the extent permitted by law, our total aggregate liability arising out of or in connection with the Service in any rolling 12-month period is limited to the greater of (a) the total fees you paid us for the Service in that period, or (b) NOK 2,000. Your mandatory statutory remedies as a Consumer are unaffected by this cap.",
        "13.4 If any part of this clause is held unenforceable, it is reduced to the minimum extent necessary to make it enforceable, and the remainder continues to apply.",
      ],
    },
    {
      title: "14. Suspension, termination and data retention",
      paragraphs: [
        "14.1 You may stop using the Service and delete your account at any time.",
        "14.2 We may suspend or terminate your access if you materially breach these Terms, where required by law, or to protect the Service or other users; where reasonable and lawful we will give notice.",
        "14.3 On termination your right to use the Service ends. We retain and delete data as described in the Privacy Policy; certain records (for example accounting and payment records) are retained for the period required by Norwegian law (generally five years under the Bookkeeping Act) and cannot be deleted earlier on request.",
      ],
    },
    {
      title: "15. Changes to these Terms",
      paragraphs: [
        "15.1 We may update these Terms. For material changes we will give reasonable notice by email or in the Service before they take effect. If you continue to use the Service after the changes take effect you accept the updated Terms; if you do not accept them you should stop using the Service and may cancel.",
      ],
    },
    {
      title: "16. Governing law, disputes and general",
      paragraphs: [
        "16.1 These Terms are governed by Norwegian law, with the ordinary Norwegian courts and Oslo District Court as legal venue. If you are a Consumer, you keep any mandatory protections and court options of your EEA country of residence.",
        "16.2 Please contact us first at contact@vikeep.no to resolve any dispute. As a Consumer you may also refer a dispute to the Norwegian Consumer Authority (Forbrukertilsynet), the Consumer Council of Norway (Forbrukerrådet), and, where applicable, the Consumer Disputes Committee (Forbrukerklageutvalget). If you are a Consumer resident elsewhere in the EEA, you may also use the out-of-court dispute resolution bodies available in your country of residence.",
        "16.3 Notices may be given by email to your account address or in the Service. Assignment: you may not assign these Terms; we may assign them to an affiliate or in connection with a merger, reorganisation, or sale of assets, provided your rights are not diminished. Severability: if any provision is invalid, the remainder continues in effect. Survival: clauses that by their nature should survive termination (including 3, 5, 7, 12, 13, 14.3, and 16) continue to apply. These Terms and the Privacy Policy are the entire agreement for the Service.",
      ],
    },
    {
      title: "17. Contact",
      paragraphs: ["VIKEEP AS · org.nr 938 008 272 · Oslo, Norway · contact@vikeep.no"],
    },
  ],
};

const CONTENT_NB: TermsContent = {
  headerLine: HEADER_LINE_NB,
  lastUpdated: LAST_UPDATED_NB,
  sections: [
    {
      title: "1. Definisjoner",
      paragraphs: [
        "1.1 «VIKEEP», «vi», «oss» — VIKEEP AS, tilbyderen av Tjenesten.",
        "1.2 «Tjenesten» / «VISIKT» — VISIKT-nettappen på visikt.com og tilhørende funksjonalitet.",
        "1.3 «Forbruker» — en bruker som opptrer utenfor sin næringsvirksomhet.",
        "1.4 «Ditt Innhold» — opplysningene du legger inn, inkludert abonnementer, lønnstall og notater.",
        "1.5 «KPI-data» — offisiell konsumprisindeks og tilhørende statistiske data som Tjenesten bruker til å beregne inflasjonsjusterte verdier.",
        "1.6 «Personvernerklæringen» — vår personvernerklæring tilgjengelig i Tjenesten, som utgjør en del av disse Vilkårene.",
      ],
    },
    {
      title: "2. Aksept og omfang",
      paragraphs: [
        "2.1 Ved å opprette en konto eller bruke Tjenesten godtar du disse Vilkårene og Personvernerklæringen. Godtar du dem ikke, skal du ikke bruke Tjenesten.",
        "2.2 Disse Vilkårene, Personvernerklæringen og pris- og planopplysningene som vises ved kjøp, utgjør hele avtalen mellom deg og oss for Tjenesten.",
        "2.3 Er du Forbruker, begrenser ingenting i disse Vilkårene rettigheter du har etter ufravikelig norsk eller EØS-forbrukerlovgivning; der et vilkår er i strid med slike rettigheter, går rettighetene foran.",
        "2.4 Besøker du de offentlige sidene til Tjenesten (for eksempel markedsføringsnettstedet) uten å opprette en konto, gjelder punkt 7 (Immaterielle rettigheter) og punkt 12 (Tilgjengelighet og ansvarsfraskrivelser) for besøket ditt på samme måte som for en Bruker. Å besøke disse offentlige sidene alene etablerer for øvrig ikke noe avtaleforhold mellom deg og oss.",
      ],
    },
    {
      title: "3. Hva VISIKT er — og ikke er",
      paragraphs: [
        "3.1 VISIKT hjelper deg å registrere abonnementer og lønn, og viser beregninger og visualiseringer, inkludert inflasjonsjustert («reell») kjøpekraft utledet fra KPI-data.",
        "3.2 Tjenesten er kun et informasjons- og oversiktsverktøy. Den gir ikke økonomisk, investerings-, skatte-, regnskaps-, pensjons-, gjelds- eller juridisk rådgivning, og ingen resultater utgjør en personlig anbefaling. Alle tall produseres ved deterministisk beregning fra opplysningene du legger inn og fra tredjeparts KPI-data; de kan være estimater, avrundede, foreløpige eller forenklede, og KPI-tall kan bli revidert av kilden etter publisering.",
        "3.3 Resultatene er generell informasjon, ikke automatiserte avgjørelser med rettsvirkning eller tilsvarende vesentlig betydning for deg, og skal ikke legges til grunn for noen økonomisk beslutning.",
        "3.4 Du er selv ansvarlig for å verifisere tallene og for dine egne beslutninger. VIKEEP er ikke en bank, betalingsforetak, regnskapsfører eller økonomisk rådgiver, og flytter, oppbevarer eller forvalter ikke pengene dine. Der en beslutning er viktig for deg, bør du innhente uavhengig, kvalifisert rådgivning.",
      ],
    },
    {
      title: "4. Hvem kan bruke Tjenesten, konto og sikkerhet",
      paragraphs: [
        "4.1 Du må være minst 18 år og i stand til å inngå en bindende avtale. Tjenesten er til personlig, ikke-kommersiell bruk, med én konto per person med mindre vi avtaler noe annet.",
        "4.2 Du skal oppgi korrekte registreringsopplysninger og holde dem oppdatert.",
        "4.3 Du er ansvarlig for å holde påloggingsinformasjonen din sikker og for aktivitet på kontoen din, og skal gi oss beskjed uten opphold på contact@vikeep.no hvis du mistenker uautorisert bruk.",
        "4.4 Du kan logge inn med e-post og passord eller med en tredjepartsleverandør (Google eller Apple); bruken av disse er også underlagt deres vilkår.",
      ],
    },
    {
      title: "5. Ditt Innhold",
      paragraphs: [
        "5.1 Mellom deg og oss eier du Ditt Innhold. Du gir oss en begrenset lisens til å lagre, behandle og vise det utelukkende for å drifte og levere Tjenesten til deg, som beskrevet i Personvernerklæringen.",
        "5.2 Du er ansvarlig for at Ditt Innhold er korrekt; Tjenestens resultater er ikke mer nøyaktige enn opplysningene dine og de tilgjengelige KPI-dataene. Ikke legg inn særlige kategorier av personopplysninger (for eksempel helse-, politisk eller religiøs informasjon) i fritekstfelt.",
        "5.3 Du kan eksportere Ditt Innhold (CSV) og slette kontoen din inne i Tjenesten. Ved sletting sletter eller anonymiserer vi Ditt Innhold uten ugrunnet opphold, unntatt opplysninger vi er rettslig forpliktet til å oppbevare (se punkt 14 og Personvernerklæringen).",
      ],
    },
    {
      title: "6. Akseptabel bruk",
      paragraphs: [
        "6.1 Du samtykker i å ikke: bruke Tjenesten ulovlig eller i strid med disse Vilkårene; skaffe eller forsøke å skaffe uautorisert tilgang til andre kontoer eller våre systemer; forstyrre Tjenesten; hente ut eller skrape andre data enn Ditt Innhold via funksjonene vi tilbyr; foreta omvendt utvikling av Tjenesten, unntatt i den grad slik begrensning er forbudt etter loven; videreselge eller kommersielt utnytte Tjenesten uten vår tillatelse; eller laste opp ulovlig innhold eller innhold du ikke har rett til å dele.",
      ],
    },
    {
      title: "7. Immaterielle rettigheter",
      paragraphs: [
        "7.1 Tjenesten, inkludert programvare, design, tekst og merkevare (VISIKT- og VIKEEP-merkene), unntatt Ditt Innhold, eies av VIKEEP eller våre lisensgivere. Vi gir deg en begrenset, ikke-eksklusiv, ikke-overførbar og tilbakekallelig rett til å bruke Tjenesten til personlig, ikke-kommersiell bruk i samsvar med disse Vilkårene.",
        "7.2 KPI-data og andre tredjepartsdata brukes under de respektive kildenes rettigheter og forblir deres eiendom.",
      ],
    },
    {
      title: "8. Planer, priser og betaling",
      paragraphs: [
        [
          { text: "8.1 Gratis plan. ", bold: true },
          {
            text: "Leveres som den er og kostnadsfritt, med funksjonsbegrensninger — for tiden inntil tre abonnementer og tre års lønnshistorikk, og inflasjonssammenligning begrenset til inneværende år. Overskrider du en grense og senere går tilbake til Gratis plan, bevares eksisterende oppføringer, men de er ikke synlige eller redigerbare før du oppgraderer igjen.",
          },
        ],
        [
          { text: "8.2 Pro-plan. ", bold: true },
          {
            text: "Låser opp ubegrenset antall oppføringer, full lønnshistorikk og full inflasjonssammenligning, fakturert løpende månedlig eller årlig i norske kroner (NOK) til prisen som vises ved kjøp. Prisene er oppgitt inkludert norsk merverdiavgift (MVA) der det er aktuelt.",
          },
        ],
        "8.3 Betalinger behandles av Stripe; vi mottar eller lagrer ikke fullstendige kortopplysninger. Ved å abonnere gir du oss (via Stripe) fullmakt til å belaste valgt betalingsmiddel for hver faktureringsperiode. Hvis en betaling mislykkes, kan vi forsøke på nytt og kan suspendere eller nedgradere Pro-tilgangen inntil betaling går gjennom.",
      ],
    },
    {
      title: "9. Fornyelse, prisendringer og oppsigelse",
      paragraphs: [
        [
          { text: "9.1 Automatisk fornyelse. ", bold: true },
          {
            text: "Pro-planen din fornyes automatisk ved slutten av hver faktureringsperiode til den til enhver tid gjeldende prisen, inntil du sier den opp. Prisen og faktureringsintervallet vises ved kjøp og før hver fornyelse.",
          },
        ],
        [
          { text: "9.2 Prisendringer. ", bold: true },
          {
            text: "Vi kan endre Pro-priser med forhåndsvarsel per e-post eller i Tjenesten; den nye prisen gjelder fra neste fornyelse, og du kan si opp før den trer i kraft.",
          },
        ],
        [
          { text: "9.3 Oppsigelse / nedgradering. ", bold: true },
          {
            text: "Du kan si opp eller nedgradere når som helst inne i Tjenesten. Oppsigelse stanser fremtidige fornyelser; Pro-tilgangen din varer ut perioden du allerede har betalt for, og deretter blir kontoen din Gratis. Med mindre loven eller punkt 10 krever det, refunderes ikke allerede betalte beløp for inneværende periode ved oppsigelse midt i perioden.",
          },
        ],
      ],
    },
    {
      title: "10. Forbrukerens angrerett",
      paragraphs: [
        "10.1 Som Forbruker som kjøper en digital tjeneste ved fjernsalg, har du en lovfestet rett til å gå fra avtalen innen 14 dager etter at avtalen ble inngått, etter angrerettloven.",
        [
          { text: "10.2 Uttrykkelig anmodning om umiddelbar levering. ", bold: true },
          {
            text: "Før noen belastning for en betalt plan blir du bedt om å (a) uttrykkelig anmode om at vi begynner å levere den betalte Tjenesten umiddelbart, og (b) bekrefte at du forstår at du taper angreretten når Tjenesten er fullt levert, og at du, dersom du går fra avtalen etter at leveringen er påbegynt, kan skylde et beløp som står i forhold til det som allerede er levert. Vi registrerer denne anmodningen og bekreftelsen.",
          },
        ],
        "10.3 Der du har gitt anmodningen og bekreftelsen og leveringen er påbegynt, taper du angreretten eller den reduseres forholdsmessig, slik loven fastsetter. Der du ikke har gitt dette, kan du gå fra avtalen innen 14 dager ved å kontakte contact@vikeep.no (du kan bruke det standardiserte angreskjemaet), og vi refunderer deg slik loven krever, uten ugrunnet opphold.",
        "10.4 Vi bekrefter den inngåtte avtalen — inkludert anmodningen og bekreftelsen over, prisen, faktureringsintervallet og fornyelsesvilkårene — på et varig medium (for eksempel per e-post eller som en nedlastbar bekreftelse) etter kjøp.",
        "10.5 Disse lovfestede rettighetene gjelder uavhengig av alt annet i disse Vilkårene.",
      ],
    },
    {
      title: "11. Tredjepartstjenester og leverandørkatalog",
      paragraphs: [
        "11.1 Tjenesten inneholder en katalog over vanlige abonnementsleverandører og kan lenke til tredjepartssider, inkludert veiledning om oppsigelse. VIKEEP er ikke tilknyttet, godkjent av eller sponset av disse leverandørene; tredjepartsnavn og varemerker tilhører sine eiere og brukes kun for identifikasjon.",
        "11.2 Vi kontrollerer ikke tredjepartstjenester og er ikke ansvarlige for deres innhold, nøyaktighet eller tilgjengelighet. VISIKT sier ikke opp, forvalter eller betaler ikke tredjepartsabonnementene dine — du må si opp direkte hos leverandøren. Lenker og veiledning tilbys for enkelhets skyld og kan bli utdatert.",
        "11.3 Innloggings- og betalingsleverandører (Google, Apple, Stripe) opererer under sine egne vilkår og personvernerklæringer.",
      ],
    },
    {
      title: "12. Tilgjengelighet og ansvarsfraskrivelser",
      paragraphs: [
        "12.1 Så langt loven tillater, og uten å begrense dine ufravikelige forbrukerrettigheter, leveres Tjenesten «som den er» og «som tilgjengelig». Vi garanterer ikke at den vil være uavbrutt eller feilfri, eller at noen beregning eller tredjepartsopplysning er korrekt, fullstendig, oppdatert eller egnet for din særlige situasjon.",
        "12.2 Vi kan endre, suspendere eller avvikle funksjoner eller Tjenesten; avvikler vi en betalt funksjon du aktivt betaler for, håndterer vi det rimelig og i tråd med dine forbrukerrettigheter. Enkelte funksjoner kan tilbys som beta eller forhåndsvisning og kan endres eller trekkes tilbake.",
        "12.3 Vi er ikke ansvarlige for svikt eller forsinkelse forårsaket av forhold utenfor vår rimelige kontroll (for eksempel driftsavbrudd hos infrastruktur- eller tredjepartsleverandører, nettverksfeil eller myndighetshandlinger). Dette påvirker ikke din rett til å si opp eller dine lovfestede forbrukerrettigheter.",
      ],
    },
    {
      title: "13. Ansvar",
      paragraphs: [
        "13.1 Ingenting i disse Vilkårene utelukker eller begrenser ansvar som ikke lovlig kan utelukkes eller begrenses etter norsk rett, herunder ansvar som skyldes vår forsett eller grove uaktsomhet, og ingenting påvirker dine ufravikelige rettigheter som Forbruker.",
        "13.2 Med forbehold for punkt 13.1 er vi ikke ansvarlige for: (a) økonomisk tap, kostnad eller beslutning som følge av at du har lagt Tjenestens informasjonsresultater til grunn; (b) indirekte tap eller følgetap, herunder tap av fortjeneste, besparelser, goodwill eller data; eller (c) tap som skyldes tredjepartstjenester, at du har lagt inn Ditt Innhold feil, eller at du ikke har sagt opp et tredjepartsabonnement.",
        "13.3 Med forbehold for punkt 13.1, og så langt loven tillater, er vårt samlede ansvar knyttet til Tjenesten i en løpende 12-månedersperiode begrenset til det høyeste av (a) det totale beløpet du har betalt oss for Tjenesten i denne perioden, eller (b) NOK 2 000. Dine lovfestede forbrukerrettigheter påvirkes ikke av denne begrensningen.",
        "13.4 Anses en del av dette punktet som ikke rettskraftig, skal den reduseres til det minimum som er nødvendig for å gjøre den rettskraftig, og resten fortsetter å gjelde.",
      ],
    },
    {
      title: "14. Suspensjon, opphør og oppbevaring av data",
      paragraphs: [
        "14.1 Du kan når som helst slutte å bruke Tjenesten og slette kontoen din.",
        "14.2 Vi kan suspendere eller avslutte tilgangen din ved vesentlig brudd på disse Vilkårene, der loven krever det, eller for å beskytte Tjenesten eller andre brukere; der det er rimelig og lovlig, gir vi varsel.",
        "14.3 Ved opphør faller retten din til å bruke Tjenesten bort. Vi oppbevarer og sletter data som beskrevet i Personvernerklæringen; enkelte opplysninger (for eksempel regnskaps- og betalingsdokumentasjon) oppbevares i den perioden norsk lov krever (som hovedregel fem år etter bokføringsloven) og kan ikke slettes tidligere, selv på forespørsel.",
      ],
    },
    {
      title: "15. Endringer i disse Vilkårene",
      paragraphs: [
        "15.1 Vi kan oppdatere disse Vilkårene. Ved vesentlige endringer gir vi rimelig varsel per e-post eller i Tjenesten før de trer i kraft. Fortsetter du å bruke Tjenesten etter at endringene har trådt i kraft, godtar du de oppdaterte Vilkårene; godtar du dem ikke, bør du slutte å bruke Tjenesten og kan si opp.",
      ],
    },
    {
      title: "16. Lovvalg, tvister og generelle bestemmelser",
      paragraphs: [
        "16.1 Disse Vilkårene reguleres av norsk rett, med de ordinære norske domstolene og Oslo tingrett som verneting. Er du Forbruker, beholder du eventuelle ufravikelige beskyttelsesregler og domstolsmuligheter i bostedslandet ditt i EØS.",
        "16.2 Kontakt oss først på contact@vikeep.no for å løse en eventuell tvist. Som Forbruker kan du også bringe en tvist inn for Forbrukertilsynet, Forbrukerrådet og, der det er aktuelt, Forbrukerklageutvalget. Er du Forbruker bosatt i et annet EØS-land, kan du også bruke de utenrettslige tvisteløsningsordningene som er tilgjengelige i bostedslandet ditt.",
        "16.3 Varsler kan gis per e-post til kontoens e-postadresse eller i Tjenesten. Overdragelse: du kan ikke overdra disse Vilkårene; vi kan overdra dem til et tilknyttet selskap eller i forbindelse med fusjon, omorganisering eller salg av virksomhet, forutsatt at dine rettigheter ikke svekkes. Delbarhet: er en bestemmelse ugyldig, fortsetter resten å gjelde. Videreføring: bestemmelser som etter sin art bør bestå etter opphør (herunder punkt 3, 5, 7, 12, 13, 14.3 og 16), fortsetter å gjelde. Disse Vilkårene og Personvernerklæringen utgjør hele avtalen for Tjenesten.",
      ],
    },
    {
      title: "17. Kontakt",
      paragraphs: ["VIKEEP AS · org.nr 938 008 272 · Oslo, Norge · contact@vikeep.no"],
    },
  ],
};

function Paragraph({ content }: { content: Rich }) {
  if (typeof content === "string") {
    return <p className="text-sm text-muted-foreground leading-relaxed">{content}</p>;
  }
  return (
    <p className="text-sm text-muted-foreground leading-relaxed">
      {content.map((part, i) => (
        <span
          key={i}
          className={
            (part.italic ? "italic " : "") + (part.bold ? "font-semibold text-foreground " : "")
          }
        >
          {part.text}
        </span>
      ))}
    </p>
  );
}

function TermsPage() {
  const { t, i18n } = useTranslation();
  const content = i18n.language?.startsWith("nb") ? CONTENT_NB : CONTENT_EN;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="max-w-2xl mx-auto w-full px-5 pt-8 flex items-center justify-between">
        <Link to="/" className="font-display font-extrabold tracking-tighter text-xl">
          VISIKT
        </Link>
        <PublicNav />
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-5 pt-14 pb-16 space-y-10">
        <section className="animate-entry">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">
            {t("terms.eyebrow")}
          </p>
          <h1 className="text-4xl font-display font-bold tracking-tight leading-[1.05]">
            {t("terms.headline")}
          </h1>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {content.lastUpdated}
          </p>
          <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{content.headerLine}</p>
        </section>

        {content.sections.map((section, si) => (
          <section key={si} className="animate-entry space-y-3">
            <h2 className="text-base font-display font-semibold tracking-tight text-foreground">
              {section.title}
            </h2>
            {section.paragraphs.map((p, pi) => (
              <Paragraph key={pi} content={p} />
            ))}
          </section>
        ))}

        <PublicFooter />
      </main>
    </div>
  );
}
